import { computedAsync, createGlobalState, until } from "@vueuse/core";
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { State, type Profile } from "../models/profile";
import { useUserMetadataStore } from "./userMetadataStore";
import { useWorkspace } from "./workspaceStore";
import { useUserSession } from "addeus-common-library/stores/userSession";
import { eventGTM } from "./eventGTM";

export type ProfileStore = ReturnType<typeof useProfile>;
export const useProfile = createGlobalState(() => {
    const workspaceStore = useWorkspace();
    const router = useRouter();
    const evaluating = ref(true);
    const { currentUserMetadata } = useUserMetadataStore();
    const userSession = useUserSession();

    const currentProfile = computedAsync<Profile | null | undefined>(
        async () => {
            if (typeof router.currentRoute.value.params.profileId !== "string") {
                return null;
            }
            await until(() => workspaceStore.evaluating.value).toBe(false);
            const workspace = workspaceStore.currentWorkspace.value;
            if (workspace === null || workspace === undefined) {
                return null;
            }
            const profileId = router.currentRoute.value.params.profileId;
            const profile = workspace.profiles.useDoc(profileId);
            await profile.$getMetadata().isFullfilling;
            

            if (currentUserMetadata.value !== null) {
                await currentUserMetadata.value.$getMetadata().refresh();   
            }

            if (profile.$getMetadata().isDeleted || profile.state === State.Inactive) {
                return null;
            }
            return profile;
        },
        undefined,
        evaluating,
    );

    watch(currentProfile, () => {
        eventGTM.emit('profileCharged', {
            profile: currentProfile.value?.name,
            profileID: currentProfile.value?.$getID(),
            workspace: workspaceStore.currentWorkspace.value?.name,
            workspaceID: workspaceStore.currentWorkspace.value?.$getID(),
            email: currentUserMetadata.value?.email,
            user_id: currentUserMetadata.value?.$getID(),
            name: `${currentUserMetadata.value?.firstName} ${currentUserMetadata.value?.lastName}`,
            phone: currentUserMetadata.value?.phoneNumber,
            isVerified: userSession.user.emailVerified,
        } as never);
    })
    return { currentProfile, evaluating };
});
